import React from 'react'
import ServicesCTA from '../components/services/ServicesCTA'
import { Helmet } from 'react-helmet'
import utils from '../utils'
import { RenderIcon } from '../components/houses/IconRenderer'
import { useApp } from '../context/AppContext'
import corporate from '../assets/corporate.jpeg'
import venue from '../assets/venue.jpeg'
import privateEvent from '../assets/private.jpeg'

function ServicesPage() {
    const { amenities } = useApp()
    return (
        <div>
            <Helmet>
                <title>Services - {utils.siteName}</title>
            </Helmet>
            <div className="bg-primary/5">
                <header className="px-8 mb-16 max-w-7xl mx-auto py-3">
                    <span className="block text-xs uppercase tracking-widest font-label text-secondary mb-4">Exceptional Living</span>
                    <h1 className="text-3xl md:text-5xl font-headline text-primary max-w-3xl leading-tight">Curated Services</h1>
                    <div className="w-24 h-1 bg-secondary mt-5"></div>
                </header>
            </div>
            <div className="max-w-7xl mx-auto space-y-10 mb-16">
                <div className="px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-7">
                        <div className="relative group">
                            <div className="absolute -inset-4 bg-surface-container-low rounded-xl -z-10"></div>
                            <img
                                className="w-full aspect-[4/3] z-0 object-cover rounded-xl shadow-sm perspective-image"
                                alt="Exquisite professional catering spread on a long oak table with gourmet appetizers, elegant glassware, and soft morning sunlight through large windows"
                                src={corporate}
                            />
                        </div>
                    </div>
                    <div className="md:col-span-5 md:pl-12">
                        <span className="text-xs text-secondary uppercase tracking-widest font-label text-outline mb-2 block">
                            Business & Dining
                        </span>
                        <h2 className="text-4xl font-headline text-primary mb-6">
                            Corporate Catering
                        </h2>
                        <p className="text-on-surface-variant leading-relaxed text-lg mb-8">
                            Elevate your professional gatherings with our full-service catering for meetings.
                            From artisanal coffee breaks to multi-course executive luncheons, we curate menus
                            that inspire productivity and impress partners.
                        </p>
                    </div>
                </div>
                <div className="px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5 md:order-1 order-2 md:pr-12 text-right">
                        <span className="text-xs text-secondary uppercase tracking-widest font-label text-outline mb-2 block">
                            Atmosphere & Design
                        </span>
                        <h2 className="text-4xl font-headline text-primary mb-6">
                            Venue Management
                        </h2>
                        <p className="text-on-surface-variant leading-relaxed text-lg mb-8">
                            Our dedicated specialists transform our sophisticated spaces into the perfect
                            backdrop for your vision. We handle technical logistics, lighting choreography,
                            and spatial design with meticulous attention to detail.
                        </p>
                    </div>
                    <div className="md:col-span-7 md:order-2 order-1">
                        <div className="relative group">
                            <div className="absolute -inset-4 bg-surface-container-low rounded-xl -z-10"></div>
                            <img
                                className="w-full aspect-[4/3] z-0 object-cover rounded-xl shadow-sm perspective-image-reverse"
                                alt="Minimalist high-end event space with architectural concrete walls, floor-to-ceiling glass, and designer lighting fixtures during blue hour"
                                src={venue}
                            />
                        </div>
                    </div>
                </div>
                <div className="px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-7">
                        <div className="relative group">
                            <div className="absolute -inset-4 bg-surface-container-low rounded-xl -z-10"></div>
                            <img
                                className="w-full aspect-[4/3] z-0 object-cover rounded-xl shadow-sm perspective-image"
                                alt="Intimate evening garden party with warm string lights, velvet seating arrangements, and elegant floral centerpieces under a clear night sky"
                                src={privateEvent}
                            />
                        </div>
                    </div>
                    <div className="md:col-span-5 md:pl-12">
                        <span className="text-xs text-secondary uppercase tracking-widest font-label text-outline mb-2 block">
                            Social & Celebration
                        </span>
                        <h2 className="text-4xl font-headline text-primary mb-6">
                            Private Events
                        </h2>
                        <p className="text-on-surface-variant leading-relaxed text-lg mb-8">
                            Create unforgettable memories in an environment of quiet luxury. Whether it is
                            an intimate engagement or a milestone celebration, our bespoke approach ensures
                            every guest feels the ethereal essence of Sage.
                        </p>
                    </div>
                </div>

            </div>
            <div className="mb-10">
                <section className="max-w-7xl mx-auto px-8 text-center mb-10">
                    <p className="font-label text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-4">
                        The Collection
                    </p>
                    <h1 className="text-4xl font-headline text-primary mb-6">
                        Amenities & Services
                    </h1>
                    <p className="max-w-2xl mx-auto text-text-muted text-lg font-light leading-relaxed">
                        Our properties have everything you need for a seamless stay. From essentials to luxury additions,
                        explore the full range of services included in our properties.
                    </p>
                </section>

                <section className="max-w-7xl mx-auto px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {amenities?.map((amenity) => (
                            <div
                                key={amenity.id}
                                className="bg-white p-8 rounded-2xl border border-gray-100 flex items-start gap-6 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 group"
                            >
                                <div className="w-14 h-14 rounded-xl bg-surface-container flex items-center justify-center text-primary shrink-0 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                                    <span className="text-2xl">
                                        <RenderIcon iconName={amenity.icon_class} />
                                    </span>
                                </div>
                                <div>
                                    <h4 className="font-bold text-primary text-sm uppercase tracking-wider mb-2">
                                        {amenity.name}
                                    </h4>
                                    <p className="text-text-muted text-sm leading-relaxed">
                                        {amenity.description || `Experience the comfort of our premium ${amenity.name.toLowerCase()} services.`}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
            <ServicesCTA />
        </div>
    )
}

export default ServicesPage